import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { getCoachLanguagesOptions } from "../../components/Forms";
import { AutocompleteSelect, SwitchField } from "../../components/Forms/Fields";
import { RHForm } from "../../components/Forms/Form";
import { Icon } from "../../components/Icon";
import { Msg, useMsg } from "../../components/Msg/Msg";
import { P } from "../../components/Typography";
import { isDevelopment } from "../../utils/environment";
import { useAuth } from "../Authorization";
import { ControlsContainer } from "../Sessions/steps/Controls";
import { TIMEZONE_OPTIONS } from "../Settings/GeneralSettings";
import { usePrimaryRolesOptions } from "../Settings/options";
import { useFieldsDict } from "../Settings/useFieldsDict";
import { useIsFavoritesLocalMode } from "./Favorites/useFavorites";
import { SmartWrap } from "./SmartWrap";

export const INITIAL_FILTER = (overrides = {}) => ({
  // languages: [userLang.substring(0, 2)],
  languages: [],
  fields: [],
  timezones: [],
  prices: [],
  primaryRoles: [],
  search: "",
  displayFavorites: false,
  ...overrides,
});

export const ratesOptions = [
  { value: "$", label: "$" },
  { value: "$$", label: "$$" },
  { value: "$$$", label: "$$$" },
];

const limitRatesOptions = (allowedCoachRates) => {
  if (!allowedCoachRates?.length) return ratesOptions;
  return ratesOptions.filter(({ value }) => allowedCoachRates.includes(value));
};

const RATES = [
  { label: "$", credits: 110 },
  { label: "$$", credits: 165 },
  { label: "$$$", credits: 275 },
];

const Rates = ({ msg, rates = RATES }) => {
  return (
    <>
      <Box display="flex">
        <Box>
          {rates.map(({ label }) => (
            <Box key={label}>{label}</Box>
          ))}
        </Box>
        <Box>
          {rates.map(({ credits }) => (
            <Box key={credits}>
              {" = "}
              {msg("coaches.filter.rate.tooltip.credits-per-session", {
                credits,
              })}
            </Box>
          ))}
        </Box>
      </Box>
      <Box textAlign="center">{msg("coaches.filter.rate.tooltip.info")}</Box>
    </>
  );
};

export const CoachesFilter = ({
  filter,
  setFilter,
  supportedLanguages = [],
  supportedFields = [],
  supportedTimezones = [],
  supportedRates = [],
  supportedPrimaryRoles = [],
  favoritesDisplayed = false,
  favoritesDisabled = false,
  allowEmptyFavorites = isDevelopment(),
  disabled,
  sx = {},
}) => {
  const { user } = useAuth();
  const msg = useMsg();
  const { fieldsOptions: definedFieldsOptions } = useFieldsDict({
    withOther: true,
  });
  const primaryRolesOptions = usePrimaryRolesOptions();
  const methods = useForm({
    defaultValues: filter,
    disabled, // TODO: everywhere
  });

  const languages = methods.watch("languages");
  const fields = methods.watch("fields");
  const timezones = methods.watch("timezones");
  const prices = methods.watch("prices");
  const primaryRoles = methods.watch("primaryRoles");
  const search = methods.watch("search");
  const displayFavorites = methods.watch("displayFavorites");

  // excluding search - user needs to click the button
  const reactiveValues = useMemo(
    () => ({
      languages,
      fields,
      timezones,
      prices,
      primaryRoles,
      displayFavorites,
    }),
    [languages, fields, timezones, prices, primaryRoles, displayFavorites],
  );
  const setFilterRef = useRef(setFilter);
  setFilterRef.current = setFilter;
  useEffect(() => {
    setFilterRef.current((filter) => ({ ...filter, ...reactiveValues }));
  }, [reactiveValues]);
  const onSearch = useCallback(
    (searchStr = search) => {
      // const root = e.target.closest(".MuiInputBase-root");
      // const input = root?.querySelector("input");
      // const value = input?.value || "";
      console.log("onSearch", { searchStr, search });
      setFilter((filter) => ({ ...filter, search: searchStr }));
    },
    [search, setFilter],
  );

  const onClearFilters = () => {
    console.log("RESETTING");
    const initialValues = INITIAL_FILTER();
    methods.setValue("languages", initialValues.languages);
    methods.setValue("fields", initialValues.fields);
    methods.setValue("timezones", initialValues.timezones);
    methods.setValue("prices", initialValues.prices);
    methods.setValue("primaryRoles", initialValues.primaryRoles);
    methods.setValue("search", initialValues.search);
    methods.setValue("displayFavorites", initialValues.displayFavorites);

    onSearch("");
  };
  useEffect(() => {
    if (!favoritesDisplayed || favoritesDisabled)
      if (!allowEmptyFavorites) methods.setValue("displayFavorites", false);
  }, [favoritesDisplayed, favoritesDisabled, methods, allowEmptyFavorites]);

  const userRatesOptions = useMemo(
    () => limitRatesOptions(user.data?.allowedCoachRates),
    [user.data?.allowedCoachRates],
  );
  const isFavoritesLocalMode = useIsFavoritesLocalMode();
  const ratesDisplayed = userRatesOptions?.length > 2;
  const filterFields = [
    {
      element: (
        <Box key="language" sx={{ width: "100%" }}>
          <P emphasized sx={{ mb: 2 }}>
            {msg("coaches.filter.language.label")}
          </P>
          <AutocompleteSelect
            name="languages"
            options={getCoachLanguagesOptions()}
            allowedValues={supportedLanguages}
            multiple
          />
        </Box>
      ),
    },
    {
      element: (
        <Box key="field" sx={{ width: "100%" }}>
          <P emphasized sx={{ mb: 2 }}>
            {msg("coaches.filter.field.label")}
          </P>
          <AutocompleteSelect
            name="fields"
            options={definedFieldsOptions}
            allowedValues={supportedFields}
            multiple
          />
        </Box>
      ),
    },
    {
      element: (
        <Box key="timezones" sx={{ width: "100%" }}>
          <P emphasized sx={{ mb: 2 }}>
            {msg("coaches.filter.timezones.label")}
          </P>
          <AutocompleteSelect
            name="timezones"
            options={TIMEZONE_OPTIONS}
            allowedValues={supportedTimezones}
            multiple
          />
        </Box>
      ),
    },
    {
      hidden: !ratesDisplayed,
      element: (
        <Box key="rate" sx={{ width: "100%" }}>
          <P emphasized sx={{ mb: 2 }}>
            {msg("coaches.filter.rate.label")}
          </P>
          <AutocompleteSelect
            name="prices"
            options={userRatesOptions}
            allowedValues={supportedRates}
            multiple
          />
        </Box>
      ),
    },
    {
      element: (
        <Box key="primaryRoles" sx={{ width: "100%" }}>
          <P emphasized sx={{ mb: 2 }}>
            {msg("coaches.filter.primaryRoles.label")}
          </P>
          <AutocompleteSelect
            name="primaryRoles"
            options={primaryRolesOptions}
            allowedValues={supportedPrimaryRoles}
            multiple
          />
        </Box>
      ),
    },
  ];

  return (
    <RHForm
      form={methods}
      onSubmit={(values) =>
        console.log("onSubmit", { values }) || onSearch(values.search)
      }
    >
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ "&:last-child": { pb: 2 } }}>
          <SmartWrap minItemWidth={185}>
            {filterFields
              .filter(({ hidden }) => !hidden)
              .map(({ element }, i) => element)}
          </SmartWrap>
          <Divider sx={{ mt: 3, mb: 2 }} />
          <ControlsContainer>
            <Button
              onClick={onClearFilters}
              variant="text"
              startIcon={<Icon name="Close" />}
              sx={{ p: 1 }}
            >
              <Msg id="coaches.filter.clear-button" />
            </Button>
          </ControlsContainer>
        </CardContent>
      </Card>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Controller
          name={"search"}
          // rules={{ minLength: 5 }}
          render={({ field, fieldState, formState }) => (
            <TextField
              // disabled
              autoFocus
              sx={{
                width: 360,
                "> .MuiInputBase-root": { bgcolor: "white" },
              }}
              label=""
              placeholder={msg("coaches.filter.search.placeholder")}
              size="small"
              {...field}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      // onClick={() => onSearch()}
                      sx={{
                        mr: -2,
                        px: 1,
                        minWidth: "auto",
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      }}
                      variant="contained"
                      type="submit"
                    >
                      <Icon name="Search" />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <Tooltip
          title={
            favoritesDisabled
              ? msg("coaches.filter.favorites.tooltip.empty", {
                  icon: (
                    <Icon
                      name={"BookmarkAddOutlined"}
                      sx={{ fontSize: "inherit" }}
                    />
                  ),
                  localText: isFavoritesLocalMode
                    ? "They will be saved locally on your machine"
                    : "",
                })
              : null
          }
        >
          <FormControlLabel
            value="start"
            control={<SwitchField name="displayFavorites" />}
            label={msg("coaches.filter.favorites.label")}
            labelPlacement="start"
            disabled={favoritesDisabled && !allowEmptyFavorites}
            sx={{
              visibility: favoritesDisplayed ? "visible" : "hidden",
            }}
          />
        </Tooltip>
      </Box>
    </RHForm>
  );
};
